import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PatientPortalFeatures from '../PatientPortalFeatures';
import SmartReminders from '../SmartReminders';
import PatientEducation from '../PatientEducation';

const PatientEngagement = () => {
  const [activeFeature, setActiveFeature] = useState(null);

  const features = [
    {
      id: 'portal',
      title: 'Patient Portal',
      description: 'Secure, user-friendly portal for patient access and communication',
      icon: '🔐',
      link: '/patient-portal-features'
    },
    {
      id: 'reminders',
      title: 'Smart Reminders',
      description: 'AI-powered appointment reminders and follow-ups',
      icon: '⏰',
      link: '/smart-reminders'
    },
    {
      id: 'education',
      title: 'Patient Education',
      description: 'Personalized educational content and treatment plans',
      icon: '📚',
      link: '/patient-education'
    }
  ];

  const metrics = [
    {
      value: "85%",
      label: "Patient Portal Adoption",
      description: "Active patient engagement"
    },
    {
      value: "92%",
      label: "Patient Satisfaction",
      description: "With communication tools"
    },
    {
      value: "40%",
      label: "No-Show Reduction",
      description: "Through smart reminders"
    },
    {
      value: "3x",
      label: "Treatment Acceptance",
      description: "With educational content"
    }
  ];

  const testimonials = [
    {
      quote: "The patient engagement tools have transformed how we communicate with our patients. The portal and smart reminders have significantly improved our efficiency.",
      author: "Dr. Sarah Johnson",
      practice: "Modern Dental Care"
    },
    {
      quote: "Our patients love the personalized educational content. It's made explaining treatments much easier and increased treatment acceptance rates.",
      author: "Dr. Michael Chen",
      practice: "Family Dental Solutions"
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E5F0F3] to-white">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
              AI-Powered Patient Engagement
            </h1>
            <p className="text-xl text-gray-600">
              Transform patient communication and education with intelligent tools
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold text-[#1E2A4A] mb-4">Key Features</h2>
              <ul className="space-y-4">
                {features.map((feature) => (
                  <li 
                    key={feature.id}
                    className={`flex items-start space-x-3 p-4 rounded-xl cursor-pointer transition-all ${
                      activeFeature === feature.id ? 'bg-orange-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setActiveFeature(feature.id)}
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-2xl">
                      {feature.icon}
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                      <Link 
                        to={feature.link}
                        className="inline-block mt-2 text-orange-500 hover:text-orange-600 font-medium"
                      >
                        Read More →
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#001233] text-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold mb-4">Benefits</h2>
              <ul className="space-y-4">
                {[
                  { value: '85%', label: 'Patient Portal Adoption' },
                  { value: '92%', label: 'Patient Satisfaction' },
                  { value: '40%', label: 'No-Show Reduction' }
                ].map((benefit, index) => (
                  <li 
                    key={index}
                    className="flex items-center space-x-3 hover:scale-105 transition-transform"
                  >
                    <div className="text-3xl font-bold text-orange-500">{benefit.value}</div>
                    <div className="text-gray-300">{benefit.label}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Content */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold text-[#1E2A4A] mb-6">How It Works</h2>
              <div className="space-y-6">
                {[
                  {
                    step: 1,
                    title: 'Personalized Experience',
                    description: 'AI analyzes patient preferences and behavior patterns'
                  },
                  {
                    step: 2,
                    title: 'Smart Communication',
                    description: 'AI optimizes timing and channels for patient interactions'
                  },
                  {
                    step: 3,
                    title: 'Continuous Engagement',
                    description: 'AI maintains patient engagement through automated follow-ups'
                  }
                ].map((step) => (
                  <div 
                    key={step.step}
                    className="flex items-start space-x-4 hover:scale-102 transition-transform"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                      {step.step}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 shadow-xl text-white">
              <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="mb-6">Transform your patient engagement with AI</p>
              <Link 
                to="/schedule-demo"
                className="block w-full bg-white text-orange-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors text-center"
              >
                Schedule a Demo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientEngagement; 